<script setup lang="ts">
    import { nextTick, onUnmounted, ref, toRaw, watch } from 'vue';
    import RecentChats from '../components/RecentChats.vue';
    import { __ } from '../stores/expressions';
    import SuggestedQuestions from '../components/SuggestedQuestions.vue';
    import ChatMessages from '../components/chat/ChatMessages.vue';
    import ChatTextarea from '../components/chat/ChatTextarea.vue';
    import SearchChats from '../components/SearchChats.vue';
    import AllFilters from '../components/filter/AllFilters.vue';
    import { useRoute, useRouter } from 'vue-router';
    import { useFilterWatch } from '../composables/useFilterWatch';
    import { useAskMeStore } from '../stores/ask-me';
    import { useSharedFilterStore } from '../stores/filter';
    import { CustomAxiosRequestConfig, http } from '../services/http';
    import { EchoFilter, FilterStoreName } from '../services/api/filter/types';
    import { IChatMessage } from '../services/api/chat/chat';
    import { useChatSignalRConnection } from '../composables/useChatSignalRConnection';
    import LoadingDots from '../components/LoadingDots.vue';
    import IconButton from '../components/IconButton.vue';
    import ActiveFiltersContainer from '../components/filter/ActiveFiltersContainer.vue';
    import EchoButton from '../components/EchoButton.vue';
    import FavoriteFilters from '../components/filter/FavoriteFilters.vue';
    import {
        CircleHelpIcon,
        ListFilterIcon,
        MessageSquareIcon,
        PlusIcon,
        SearchIcon,
        XIcon,
    } from '@lucide/vue';
    import { usePermissions } from '@/js/composables/usePermissions';
    import { AskMeModulePermissionFlag, PermissionArea } from '../consts/permissions';

    const route = useRoute();
    const askMe = useAskMeStore();
    const router = useRouter();
    const loading = ref(true);

    const {
        filterStore: filter,
        filterLoaded,
        updateFilterTimeout,
    } = useFilterWatch(FilterStoreName.AskMeFilter, {
        fetchOnInit: false,
        onDebounce: (store) => {
            if (route.params.chat && route.params.chat !== 'new-chat') {
                store.updateFilter(route.params.chat as string);
            } else {
                const sharedFilter = useSharedFilterStore();
                sharedFilter.setFilter(
                    store.all ? (structuredClone(toRaw(store.all)) as EchoFilter) : null
                );
                store.getFilter();
            }
        },
    });
    const { hasPermission } = usePermissions();

    const { connection: signalRConnection, switchChat, disconnect } = useChatSignalRConnection();

    signalRConnection.on('ReceiveMessage', (data: IChatMessage) => {
        console.log('signalRConnection', data);
        askMe.receiveChatMessage(data);
    });

    let navToken = 0;

    onUnmounted(() => {
        navToken++;
        askMe.clearMessages();
        signalRConnection.off('ReceiveMessage');
        disconnect().catch((err) => console.error('Failed to stop connection:', err));
    });

    watch(
        () => route.params.chat,
        async (newChatId, oldChatId) => {
            filterLoaded.value = false;
            clearTimeout(updateFilterTimeout.value);
            filter.abortPendingRequests();

            const token = ++navToken;

            const effectiveOldChatId =
                oldChatId && oldChatId !== 'new-chat' ? (oldChatId as string) : undefined;
            const effectiveNewChatId =
                newChatId && newChatId !== 'new-chat' ? (newChatId as string) : undefined;
            const newChat = !effectiveOldChatId;

            askMe.updateCurrentChatId(effectiveNewChatId);

            if (newChat && askMe.messages.length === 1) {
                loading.value = false;
            } else {
                loading.value = true;
            }

            // Not gated on effectiveOldChatId: it's undefined on a fresh mount. Keyed on the `'_'`
            // local-echo id, not just "one pending question".
            if (!(askMe.messages.length === 1 && askMe.messages[0].id === '_')) {
                askMe.clearMessages();
            }

            if (!effectiveNewChatId) {
                filter.seedFromShared();

                await nextTick();
                if (token !== navToken) return;

                askMe.clearMessages();
                askMe.loading = false;

                filter.getFilter().then(() => {
                    if (token !== navToken) return;
                    filterLoaded.value = true;
                    loading.value = false;
                });
            } else {
                const config: CustomAxiosRequestConfig = {};

                if (askMe.messages.length > 0) {
                    askMe.loading = true;
                    config.silent = true;
                }

                http.get(`chat/${effectiveNewChatId}`, config)
                    .then(async (response) => {
                        if (token !== navToken) return;
                        if (route.params.chat === response.data.id) {
                            filter.loadFilter(response.data.filter as EchoFilter);

                            const messages = response.data.messages as IChatMessage[];
                            // The fallback poll still covers it even if switchChat below fails.
                            askMe.applyFetchedMessages(response.data.id as string, messages);

                            await nextTick();
                            if (token !== navToken) return;
                            filterLoaded.value = true;
                            loading.value = false;
                        }
                    })
                    .catch((error) => {
                        if (token !== navToken) return;
                        askMe.loading = false;
                        loading.value = false;

                        if (error.status === 404) {
                            router.push('/ask-me/new-chat');
                        }
                    });
            }

            if (token !== navToken) return;

            try {
                await switchChat(effectiveNewChatId, effectiveOldChatId, () => token !== navToken);
            } catch (err) {
                if (token !== navToken) return;
                console.error('Connection failed:', err);
                loading.value = false;
            }
        },
        { immediate: true }
    );

    const visiblePanel = ref<'suggested-questions' | 'filters' | 'search-chats' | ''>('');

    watch(
        () => askMe.filters_visible,
        (isVisible) => {
            if (isVisible) {
                visiblePanel.value = 'filters';
            } else if (visiblePanel.value === 'filters') {
                visiblePanel.value = '';
            }
        }
    );

    watch(
        () => visiblePanel.value,
        (panel) => {
            if (panel === 'filters') {
                askMe.filters_visible = true;
            } else {
                askMe.filters_visible = false;
            }
        }
    );

    const handleSuggestedQuestion = (prompt: string) => {
        if (askMe.loading) {
            return;
        }

        // TODO: When backend supports more message types, change messageType to 'Prompt'
        const message: IChatMessage = {
            messageType: 'Text', // Prompt
            isUserMessage: true,
            message: prompt,
            content: { prompt },
        };

        askMe.addMessageToCurrentChat(message);

        if (!route.params.chat || route.params.chat === 'new-chat') {
            askMe.createNewChat(message, FilterStoreName.AskMeFilter).then((response) => {
                router.push(`/ask-me/${response.data.id}`);
            });
        } else {
            askMe.sendChatMessage(route.params.chat as string, message.message as string);
        }
    };

    const hasAskMeAccess = hasPermission(
        PermissionArea.AskMeModule,
        AskMeModulePermissionFlag.Access
    );

    const showAddFavoriteModal = ref(false);
</script>

<template>
    <div class="bg-white h-screen flex flex-row Ask-me overflow-hidden w-full relative">
        <div class="text-white relative z-40 w-70 flex-col hidden sm:flex flex-shrink-0 order-1">
            <h1
                class="absolute top-0 left-0 w-full pb-10 z-20 bg-linear-180 from-primary-beet from-70% to-transparent flex text-white pt-5 gap-2 items-center heading-md px-6"
            >
                <MessageSquareIcon class="size-5 mt-1" />
                {{ __('ask-me') }}
            </h1>

            <RecentChats />
        </div>

        <nav
            class="flex lg:justify-end lg:w-70 absolute top-0 right-0 lg:right-auto z-50 pt-1 pr-1 lg:pt-3 h-16 lg:text-white"
            :aria-label="__('ask-me-toolbar')"
        >
            <div class="flex">
                <button
                    v-if="hasAskMeAccess"
                    class="Icon-button lg:hidden"
                    :aria-label="__('widget-show-filters')"
                    @click="visiblePanel = 'filters'"
                >
                    <ListFilterIcon class="size-5" />
                </button>

                <button
                    class="Icon-button lg:hidden"
                    :aria-label="__('suggested-questions')"
                    @click="visiblePanel = 'suggested-questions'"
                >
                    <CircleHelpIcon class="size-5" />
                </button>

                <button
                    class="Icon-button"
                    :aria-label="__('search-chats')"
                    @click="visiblePanel = 'search-chats'"
                >
                    <SearchIcon class="size-5" />
                </button>

                <RouterLink
                    v-if="hasAskMeAccess"
                    to="/ask-me/new-chat"
                    class="Icon-button"
                    :aria-label="__('ask-me-new-chat')"
                >
                    <PlusIcon class="size-5" />
                </RouterLink>
            </div>
        </nav>

        <div
            v-if="hasAskMeAccess"
            class="absolute bg-white right-0 top-0 bottom-0 z-40 lg:right-80 2xl:right-auto 2xl:relative xl:z-40 order-3"
            :class="askMe.filters_visible ? 'w-80' : 'w-auto'"
        >
            <AllFilters
                :is-visible="askMe.filters_visible"
                :filter-store-name="FilterStoreName.AskMeFilter"
                @toggle-visibility="(visible) => (askMe.filters_visible = visible)"
            />
        </div>

        <aside
            class="absolute right-0 top-0 bottom-0 lg:relative bg-white overflow-hidden lg:w-80 lg:px-6 shadow border-l border-gray-lightest pt-6 pb-6 overflow-y-auto custom-scrollbar flex-shrink-0 order-4 z-40"
            :class="visiblePanel === 'suggested-questions' ? 'w-80 px-4' : 'w-0'"
            :aria-label="__('suggested-questions')"
        >
            <IconButton
                :aria-label="__('close')"
                size="size-6"
                class="right-0 top-5.5 absolute lg:hidden"
                @click="visiblePanel = ''"
            >
                <XIcon class="size-5" />
                <span class="sr-only">{{ __('hide-suggested-questions') }}</span>
            </IconButton>

            <h2 class="text-sm font-semibold mb-1">
                {{ __('suggested-questions') }}
            </h2>
            <p class="text-sm mb-5">
                {{ __('suggested-questions-description') }}
            </p>

            <SuggestedQuestions @select="handleSuggestedQuestion" />
        </aside>

        <Transition name="fade">
            <div
                v-if="['filters', 'suggested-questions'].includes(visiblePanel)"
                class="absolute bg-gray-dark/20 z-30 top-0 left-0 right-0 lg:right-80 bottom-0 2xl:hidden"
                @click.self="visiblePanel = ''"
            />
        </Transition>

        <div class="flex-grow w-0 flex flex-col relative justify-end order-2">
            <div v-if="loading" class="absolute z-100 top-1/2 -mt-16 left-0 w-full">
                <LoadingDots />
            </div>

            <div v-if="!loading" class="flex">
                <div class="bg-white border-b border-gray-lightest flex flex-grow pt-4 -mt-px">
                    <ActiveFiltersContainer
                        :filter-store-name="FilterStoreName.AskMeFilter"
                        :readonly="!hasAskMeAccess"
                        :all-filters-visible="askMe.filters_visible"
                    >
                        <template v-if="hasAskMeAccess" #save-filter>
                            <EchoButton
                                variant="primary"
                                size="small"
                                :title="__('favorite-filters-save')"
                                @click="showAddFavoriteModal = true"
                            >
                                {{ __('favorite-filters-save') }}
                            </EchoButton>
                        </template>
                        <template #favorite-filters>
                            <FavoriteFilters
                                v-model:show-add-modal="showAddFavoriteModal"
                                :filter-store-name="FilterStoreName.AskMeFilter"
                            />
                        </template>
                    </ActiveFiltersContainer>
                </div>
            </div>
            <ChatTextarea v-if="!loading" :filter-store-name="FilterStoreName.AskMeFilter" />
            <ChatMessages />
        </div>

        <SearchChats :show="visiblePanel === 'search-chats'" @close="visiblePanel = ''" />
    </div>
</template>
